import {
  ILeaderboardDataViewModel,
  ILeaderboardRankingDisplayModel,
  ILeaderboardRankingViewModel,
  IUserLeaderboardViewModel,
  LeaderboardMetric,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import { getBoundedValue } from '../../helpers';

export function isLeaderboardUpcoming(startDate: Date): boolean {
  const now = moment.utc();
  const momentStartDate = moment.utc(startDate);
  return momentStartDate >= now;
}

export function calculateTimeLeftText(
  startDate: Date,
  endDate: Date,
  shouldFloor: boolean = true,
):
{ unit: number; timeUnit: string, isUpcoming: boolean } {
  const now = moment.utc();
  const momentStartDate = moment.utc(startDate);

  let targetDate = moment.utc(endDate);
  if (isLeaderboardUpcoming(startDate)) {
    targetDate = momentStartDate;
  }

  const duration = moment.duration(targetDate.diff(now));

  const daysLeft = duration.asDays();

  if (daysLeft >= 1) {
    const daysToReturn = shouldFloor ? Math.floor(daysLeft) : Math.ceil(daysLeft);
    return {
      isUpcoming: momentStartDate > now,
      unit: daysToReturn,
      timeUnit: daysToReturn === 1 ? 'day' : 'days',
    };
  }
  const hoursLeft = duration.asHours();

  const ceilHoursLeft = Math.ceil(hoursLeft);
  return {
    isUpcoming: momentStartDate > now,
    unit: Math.ceil(hoursLeft),
    timeUnit: ceilHoursLeft === 1 ? 'hour' : 'hours',
  };
}

export function calculateTimelineText(
  startDate: Date,
  endDate: Date,
  timezone: string,
  displayTimeZone = true,
): string {
  const startMoment = moment(startDate).tz(timezone);
  const endMoment = moment(endDate).tz(timezone);

  const startDay = startMoment.date();
  const endDay = endMoment.date();

  const startMonth = startMoment.format('MMM');
  const endMonth = endMoment.format('MMM');

  let dateText = `${startDay} ${startMonth}`;

  const isSameDay = startMoment.isSame(endMoment, 'day');
  if (!isSameDay) {
    dateText += ` to ${endDay} ${endMonth}`;
  }

  if (!displayTimeZone) {
    return dateText;
  }

  const now = moment().tz(timezone);
  const abv = now.format('z');

  dateText += ` (${abv})`;
  return dateText;
}

export function calculatePercentageLeft(startDate: Date, endDate: Date): number {
  const startMoment = moment.utc(startDate);
  const endMoment = moment.utc(endDate);
  const now = moment.utc();

  const totalDuration = moment.duration(endMoment.diff(startMoment));
  const elapsedDuration = moment.duration(now.diff(startMoment));

  const percentageLeft = (elapsedDuration.asMilliseconds() / totalDuration.asMilliseconds()) * 100;

  return getBoundedValue(percentageLeft, 0, 100);
}

export function getPositionSuffix(position: number) {
  if (position % 100 >= 11 && position % 100 <= 13) {
    return 'th';
  }

  switch (position % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export const getInitials = (name: string): string => {
  return name.slice(0, 2).toUpperCase();
};

export const isTop3 = (rank: number) => {
  return rank <= 3;
};

function calculateEllipses(leaderboard: IUserLeaderboardViewModel, rows: ILeaderboardRankingDisplayModel[]) {
  rows.forEach((currentRanking, index) => {
    const currentRank = currentRanking.rank;
    const rankBeforeCurrentRank = rows[index - 1]?.rank;
    const hasGapBetweenCurrentRankAndRankBefore = !!rankBeforeCurrentRank && currentRank - rankBeforeCurrentRank > 1;
    if (hasGapBetweenCurrentRankAndRankBefore) {
      const lastElementInTopRow = rows[index - 1];
      lastElementInTopRow.showEllipseBottom = true;
    }

    if (rows.length > 0) {
      const lastPosition = rows[rows.length - 1];
      lastPosition.showEllipseBottom = lastPosition.rank < leaderboard.rankings.length;
    }
  });
}

export function getRankingsDisplay(
  leaderboard: IUserLeaderboardViewModel,
  summaryView: boolean,
): ILeaderboardRankingDisplayModel[] {
  let indexOfUserRank;
  const mappedRankings: ILeaderboardRankingDisplayModel[] =
    leaderboard.rankings
      .map((ranking: ILeaderboardRankingViewModel, index: number): ILeaderboardRankingDisplayModel => {
        const isMine = !!ranking.isMine && ranking.isMine;
        if (isMine) {
          indexOfUserRank = index + 1;
        }

        return {
          ...ranking,
          isMine,
          showEllipseBottom: false,
        };
      });

  if (leaderboard.competitionType === 'inter-team' && !summaryView) {
    return mappedRankings;
  }

  let rows = mappedRankings;

  if (summaryView) {
    const topRows = 3;
    rows = mappedRankings.slice(0, topRows);
    const userRanking: ILeaderboardRankingDisplayModel | undefined =
      mappedRankings.find((ranking) => ranking.isMine);
    const isUserInTopRows = !!indexOfUserRank && indexOfUserRank <= topRows;

    if (!isUserInTopRows && userRanking && indexOfUserRank) {
      rows.push(userRanking);
    }
  }

  calculateEllipses(leaderboard, rows);

  return rows;
}

export function isCompleted(endDate: Date): boolean {
  return Date.now() >= endDate.getTime();
}

export function getLeaderboardMetricText(
  metric: LeaderboardMetric,
): { abbreviation: string, description: string } {
  switch (metric) {
    case 'average-minutes':
      return {
        abbreviation: 'mins',
        description: 'time',
      };
    case 'effort-points':
      return {
        abbreviation: 'pts',
        description: 'points',
      };
    case 'total-minutes':
      return {
        abbreviation: 'mins',
        description: 'time',
      };
    case 'team-weighted-minutes-score':
      return {
        abbreviation: '',
        description: 'time-per-member',
      };
    case 'team-weighted-points-score':
      return {
        abbreviation: '',
        description: 'points-per-member',
      };
  }

  // forward compatibility when a new metric is added
  return {
    abbreviation: metric,
    description: metric,
  };
}

export function getLeaderboardRankingValue(
  metricAbbreviation: string,
  value: number,
  isTeamRanking: boolean,
): number {
  switch (metricAbbreviation) {
    case 'pts':
      return value;
    case '':
      return Number(value.toFixed(2));
  }
  // forward compatibility when a new metric is added
  return isTeamRanking ? Number(value.toFixed(2)) : value;
}

export function getLeaderboardSecondaryHeader(leaderboard: ILeaderboardDataViewModel): string {
  if (leaderboard.frequency !== 'one-time') {
    return `${leaderboard.frequency} Challenge`;
  }

  return leaderboard.name;
}

export function getTotalParticipantHeaderText(leaderboard: IUserLeaderboardViewModel): string {
  if (leaderboard.teams) {
    const totalOtherTeams = leaderboard.teams.length - 1;
    return `Compete against ${totalOtherTeams} other team${totalOtherTeams > 1 ? 's' : ''}`;
  }

  if (new Date(leaderboard.startDate) >= new Date()) {
    const timeLeft = calculateTimeLeftText(new Date(leaderboard.startDate), new Date(), false);
    return `Kicks off in ${timeLeft.unit} ${timeLeft.timeUnit}`;
  }

  if (leaderboard.totalParticipants === 0) {
    return 'No Active Participants yet';
  }

  const totalParticipants = leaderboard.totalParticipants;
  return `${totalParticipants} participant${totalParticipants > 1 ? 's' : ''} so far`;
}

export function getLeaderboardDescriptionProps(leaderboard: IUserLeaderboardViewModel): {
  denominator: string,
  metric: string,
} {
  let denominator = 'most';
  let metric = 'minutes';
  switch (leaderboard.metric) {
    case 'average-minutes':
      denominator = 'highest';
      metric = 'average workout minutes per day per member';
      break;
    case 'effort-points':
      denominator = 'most';
      metric = 'points';
      break;
    case 'total-minutes':
      denominator = 'most';
      metric = 'minutes';
      break;
  }

  return {
    denominator,
    metric,
  };
}

export function getTimeframeHeaderText(leaderboard: IUserLeaderboardViewModel): string {
  return calculateTimelineText(leaderboard.startDate, leaderboard.endDate, leaderboard.timezone, false);
}

export const shouldShowJoinLeaderboardTile = (leaderboard: IUserLeaderboardViewModel) => {
  return leaderboard.requireOptIn;
};
